import { lazy, Suspense } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router";
import { queryClient } from "./lib/query";
import { MediaProvider } from "./ui/kit";
import { CaptureContextProvider, ComposerProvider } from "./shell/Composer";
import { AppShell } from "./shell/Layout";
import { Today } from "./shell/pages/Today";
import { CattleHome, GroupPage } from "./shell/pages/Cattle";
import { AnimalPage } from "./shell/pages/Animal";
import { JobPage, TasksPage, WorkHome, WorkspaceRoute } from "./shell/pages/Work";
import { AssetPage, ContactPage, EquipmentPage, PeoplePage, PlacePage, PlacesPage, RanchHome, RanchSectionRoute, StockPage, SuppliesPage } from "./shell/pages/Ranch";
import { EventPage, LogPage, ReviewPage } from "./shell/pages/Records";
import { Join, Settings, SignIn, StartRanch } from "./shell/pages/Account";

// Read-only pages for buyers, outside the signed-in app: a sale catalog and a bull's records.
const PublicCatalog = lazy(() => import("../modules/sales/web/Catalog").then((m) => ({ default: () => <m.PublicCatalog /> })));
const PublicPacket = lazy(() => import("../modules/sales/web/Packets").then((m) => ({ default: m.PublicPacket })));
import { SearchPage } from "./shell/pages/Search";
import { NotFound } from "./shell/pages/NotFound";

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MediaProvider>
        <BrowserRouter>
          <CaptureContextProvider>
            <ComposerProvider>
              <Routes>
                <Route path="/signin" element={<SignIn />} />
                <Route path="/join/:code" element={<Join />} />
                <Route path="/start" element={<StartRanch />} />
                <Route path="/catalog/:token" element={<Suspense fallback={null}><PublicCatalog /></Suspense>} />
                <Route path="/records/:token" element={<Suspense fallback={null}><PublicPacket /></Suspense>} />
                <Route element={<AppShell />}>
                  <Route index element={<Today />} />
                  <Route path="review" element={<ReviewPage />} />
                  <Route path="history" element={<LogPage />} />
                  <Route path="history/:id" element={<EventPage />} />
                  <Route path="cattle" element={<CattleHome />} />
                  <Route path="cattle/groups/:id" element={<GroupPage />} />
                  <Route path="cattle/:id" element={<AnimalPage />} />
                  <Route path="work" element={<WorkHome />} />
                  <Route path="work/tasks" element={<TasksPage />} />
                  <Route path="work/jobs/:id" element={<JobPage />} />
                  <Route path="work/:workspace/*" element={<WorkspaceRoute />} />
                  <Route path="ranch" element={<RanchHome />} />
                  <Route path="ranch/places" element={<PlacesPage />} />
                  <Route path="ranch/places/:id" element={<PlacePage />} />
                  <Route path="ranch/supplies" element={<SuppliesPage />} />
                  <Route path="ranch/supplies/:id" element={<StockPage />} />
                  <Route path="ranch/equipment" element={<EquipmentPage />} />
                  <Route path="ranch/equipment/:id" element={<AssetPage />} />
                  <Route path="ranch/people" element={<PeoplePage />} />
                  <Route path="ranch/people/:id" element={<ContactPage />} />
                  <Route path="ranch/:section/*" element={<RanchSectionRoute />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="search" element={<SearchPage />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </ComposerProvider>
          </CaptureContextProvider>
        </BrowserRouter>
      </MediaProvider>
    </QueryClientProvider>
  );
}
